import { useState, useCallback, useRef, useEffect, startTransition } from 'react';
import { VISIBILITY_UPDATE_THROTTLE, FULL_MOUNT_BATCH, LIVE_MOUNT_BATCH, PAN_CATCHUP_MS } from './constants';
import { visibleSetsEqual } from './geometry';
import { buildVisibleNodeSet } from './buildVisibleNodeSet';
import { planCardTiers, planPanCatchUp, commitPanCatchUpBatch } from './cardTiers';

/**
 * Mounted ids (full cards + silhouettes) and the full-card subset.
 * New full cards land FULL_MOUNT_BATCH per frame after the camera settles.
 * During a pan, cards already up stay up. Newcomers mount one at a time, off the pan frame.
 */
export function useVisibleNodeSet({
  tasks,
  positionsToUse,
  nodeDimensions,
  containerRef,
  panRef,
  zoomRef,
  containerRectRef,
  visibleBoundsRef,
  selectedNodes,
  draggingNode,
  connectingFrom,
  hoveredNodeId,
  isAnimatingLayout,
  isAnimatingIntoFocus,
  isAnimatingOutOfFocus,
}) {
  const [visibleNodeIds, setVisibleNodeIds] = useState(() => new Set(tasks.map((t) => t.id)));
  const [fullNodeIds, setFullNodeIds] = useState(() => new Set(tasks.map((t) => t.id)));
  const lastUpdateRef = useRef(0);
  const pendingUpdateRef = useRef(null);
  const mountedRef = useRef(visibleNodeIds);
  const fullRef = useRef(fullNodeIds);
  const promoteQueueRef = useRef([]);
  const promoteRafRef = useRef(null);
  const catchUpTimerRef = useRef(null);
  const planGenRef = useRef(0);
  const gesturingRef = useRef(false);

  mountedRef.current = visibleNodeIds;
  fullRef.current = fullNodeIds;

  const cancelPromote = useCallback(() => {
    if (promoteRafRef.current != null) {
      cancelAnimationFrame(promoteRafRef.current);
      promoteRafRef.current = null;
    }
  }, []);

  const cancelCatchUp = useCallback(() => {
    if (catchUpTimerRef.current != null) {
      clearTimeout(catchUpTimerRef.current);
      catchUpTimerRef.current = null;
    }
  }, []);

  const schedulePromote = useCallback(() => {
    if (promoteRafRef.current != null || gesturingRef.current) return;
    if (promoteQueueRef.current.length === 0) return;
    const gen = planGenRef.current;
    const step = () => {
      promoteRafRef.current = null;
      if (gen !== planGenRef.current || gesturingRef.current) return;
      const batch = promoteQueueRef.current.splice(0, FULL_MOUNT_BATCH);
      if (batch.length === 0) return;
      startTransition(() => {
        setFullNodeIds((prev) => {
          const next = new Set(prev);
          let changed = false;
          batch.forEach((id) => {
            if (!mountedRef.current.has(id) || next.has(id)) return;
            next.add(id);
            changed = true;
          });
          if (!changed) return prev;
          fullRef.current = next;
          return next;
        });
      });
      if (promoteQueueRef.current.length > 0 && gen === planGenRef.current) {
        promoteRafRef.current = requestAnimationFrame(step);
      }
    };
    promoteRafRef.current = requestAnimationFrame(step);
  }, []);

  useEffect(() => () => {
    cancelPromote();
    cancelCatchUp();
  }, [cancelPromote, cancelCatchUp]);

  const computeVisibleSet = useCallback(
    (prevVisible) =>
      buildVisibleNodeSet({
        tasks,
        positionsToUse,
        nodeDimensions,
        container: containerRef?.current,
        containerRectRef,
        visibleBoundsRef,
        panRef,
        zoomRef,
        prevVisible,
        selectedNodes,
        draggingNode,
        connectingFrom,
        hoveredNodeId,
        isAnimatingLayout,
        isAnimatingIntoFocus,
        isAnimatingOutOfFocus,
      }),
    [
      tasks,
      positionsToUse,
      nodeDimensions,
      containerRef,
      containerRectRef,
      visibleBoundsRef,
      panRef,
      zoomRef,
      selectedNodes,
      draggingNode,
      connectingFrom,
      hoveredNodeId,
      isAnimatingLayout,
      isAnimatingIntoFocus,
      isAnimatingOutOfFocus,
    ],
  );

  const updateVisibility = useCallback(
    (force = false) => {
      const now = performance.now();
      const timeSinceLastUpdate = now - lastUpdateRef.current;

      if (pendingUpdateRef.current) {
        cancelAnimationFrame(pendingUpdateRef.current);
        pendingUpdateRef.current = null;
      }

      if (!force && timeSinceLastUpdate < VISIBILITY_UPDATE_THROTTLE) {
        pendingUpdateRef.current = requestAnimationFrame(() => {
          updateVisibility(true);
        });
        return;
      }

      lastUpdateRef.current = now;
      const target = computeVisibleSet(mountedRef.current);
      const planned = planCardTiers(fullRef.current, target);
      promoteQueueRef.current = planned.queue;
      planGenRef.current += 1;
      cancelPromote();
      mountedRef.current = planned.mounted;
      fullRef.current = planned.full;

      startTransition(() => {
        setVisibleNodeIds((prev) => (
          visibleSetsEqual(prev, planned.mounted) ? prev : planned.mounted
        ));
        setFullNodeIds((prev) => (
          visibleSetsEqual(prev, planned.full) ? prev : planned.full
        ));
      });

      if (!gesturingRef.current && planned.queue.length > 0) schedulePromote();
    },
    [computeVisibleSet, cancelPromote, schedulePromote],
  );

  // Pan frames only arm this timer. The timeout mounts one newcomer, then arms the next.
  // Cards already on the canvas are left alone until the gesture goes idle.
  const followCameraRef = useRef(() => {});

  const runCatchUpRef = useRef(() => {});
  runCatchUpRef.current = () => {
    if (!gesturingRef.current) return;

    const target = computeVisibleSet(mountedRef.current);
    const planned = planPanCatchUp(mountedRef.current, fullRef.current, target);
    if (planned.queue.length === 0) return;

    const batch = planned.queue.slice(0, LIVE_MOUNT_BATCH);
    const next = commitPanCatchUpBatch(planned.mounted, planned.full, batch, target);
    const mountedChanged = !visibleSetsEqual(mountedRef.current, next.mounted);
    const fullChanged = !visibleSetsEqual(fullRef.current, next.full);
    if (mountedChanged || fullChanged) {
      mountedRef.current = next.mounted;
      fullRef.current = next.full;
      startTransition(() => {
        if (mountedChanged) setVisibleNodeIds(next.mounted);
        if (fullChanged) setFullNodeIds(next.full);
      });
    }

    followCameraRef.current();
  };

  const followCamera = useCallback(() => {
    if (catchUpTimerRef.current != null || !gesturingRef.current) return;
    catchUpTimerRef.current = setTimeout(() => {
      catchUpTimerRef.current = null;
      runCatchUpRef.current();
    }, PAN_CATCHUP_MS);
  }, []);
  followCameraRef.current = followCamera;

  const forceUpdate = useCallback(() => {
    updateVisibility(true);
  }, [updateVisibility]);

  const startInteraction = useCallback(() => {
    gesturingRef.current = true;
    cancelPromote();
  }, [cancelPromote]);

  const endInteraction = useCallback(() => {
    gesturingRef.current = false;
    cancelCatchUp();
    updateVisibility(true);
  }, [updateVisibility, cancelCatchUp]);

  const setGesturing = useCallback((active) => {
    gesturingRef.current = active;
    if (active) cancelPromote();
    else cancelCatchUp();
  }, [cancelPromote, cancelCatchUp]);

  return {
    visibleNodeIds,
    fullNodeIds,
    updateVisibility,
    forceUpdate,
    startInteraction,
    endInteraction,
    setGesturing,
    followCamera,
  };
}
